import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, FormField } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import {
  IMPERSONATABLE_AUDIENCES,
  impersonationApi,
  type EnsureQaUserRequest,
  type EnsureQaUserResultDto,
} from "@/api/impersonation";
import { qaUserKeys } from "@/hooks/impersonation/impersonationKeys";
import { LogInAsAction, type LogInAsTarget } from "@/components/impersonation/LogInAsAction";
import { useRoles } from "@/hooks/useRoles";

// Admin User Impersonation — QA user provisioning.
//
// This exists because most role codes have NO real user behind them: filtering
// /admin/users by B3 or E1 returns an empty table, so there is nobody to
// impersonate and no way to see what that role's app looks like. "Ensure"
// (not "create") is the honest verb — the endpoint is idempotent and reports
// via `created` whether it minted an account or found one already there.

interface Props {
  onClose: () => void;
  /** Preselects a staff role — used by the empty state of a filtered roster,
   *  where the admin has already said which role they were looking for. */
  initialRoleCode?: string;
}

type Mode = "staff" | "portal";

export function EnsureQaUserModal({ onClose, initialRoleCode }: Props) {
  const qc = useQueryClient();
  const rolesQuery = useRoles();
  const roles = rolesQuery.data ?? [];

  const [mode, setMode] = useState<Mode>(initialRoleCode ? "staff" : "staff");
  const [roleCode, setRoleCode] = useState(initialRoleCode ?? "");
  const [accountType, setAccountType] = useState<string>("");
  const [result, setResult] = useState<EnsureQaUserResultDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ensure = useMutation({
    mutationFn: (body: EnsureQaUserRequest) => impersonationApi.ensureQaUser(body),
    onSuccess: (res) => {
      setResult(res);
      // A newly-minted QA account has to appear in the roster the admin came
      // from, or they will be looking at the same empty table that sent them
      // here. Keys live in the module's *Keys factory per the stale-after-save
      // gate; the two rosters are disjoint, so only the relevant one is hit.
      void qc.invalidateQueries({
        queryKey:
          res.kind === "staff"
            ? qaUserKeys.staffRoster()
            : qaUserKeys.portalAccounts(),
      });
    },
    onError: (err: unknown) => setError(extractErrorMessage(err)),
  });

  // Exactly one of the two is sent — the endpoint rejects both or neither.
  const selection = mode === "staff" ? roleCode : accountType;
  const canSubmit = selection.length > 0 && !ensure.isPending;

  const impersonationTarget: LogInAsTarget | null = !result
    ? null
    : result.kind === "staff"
      ? {
          kind: "staff",
          userId: result.id,
          label: result.displayName,
          roleCodes: result.roleCode ? [result.roleCode] : [],
        }
      : {
          kind: "portal",
          portalUserId: result.id,
          label: result.displayName,
          accountType: result.accountType ?? "",
        };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="QA test account"
      description="Find or create a throwaway account for a role or portal type, so there is somebody to impersonate while testing."
      size="md"
      footer={
        result ? (
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={ensure.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setError(null);
                ensure.mutate(
                  mode === "staff" ? { roleCode } : { accountType },
                );
              }}
              loading={ensure.isPending}
              disabled={!canSubmit}
            >
              Find or create
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="grid gap-space-3">
          <p className="m-0 text-small text-onyx">
            {result.created
              ? "Created a new QA account."
              : "A QA account already existed — reusing it."}
          </p>
          <dl className="grid gap-space-2 m-0">
            <ResultRow label="Name">{result.displayName}</ResultRow>
            <ResultRow label="Email">
              <span className="font-mono">{result.email}</span>
            </ResultRow>
            <ResultRow label={result.kind === "staff" ? "Role code" : "Portal type"}>
              {result.roleCode ?? result.accountType ?? "—"}
            </ResultRow>
          </dl>
          {impersonationTarget && (
            <div className="flex items-center gap-space-3 border-t border-border-light pt-space-3">
              <span className="text-caption text-gray-mid">
                Sign in as this account now?
              </span>
              <LogInAsAction target={impersonationTarget} />
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-space-3">
          <div className="flex flex-wrap gap-space-4">
            <ModeRadio
              checked={mode === "staff"}
              onSelect={() => setMode("staff")}
              label="Staff role"
            />
            <ModeRadio
              checked={mode === "portal"}
              onSelect={() => setMode("portal")}
              label="Portal account type"
            />
          </div>

          {mode === "staff" ? (
            <FormField
              label="Role code"
              required
              htmlFor="qa-role-code"
              helperText="Read from the live role catalogue, so a role added in /admin/roles is offered here without a redeploy."
            >
              <Select
                id="qa-role-code"
                value={roleCode}
                onChange={(e) => setRoleCode(e.target.value)}
              >
                <option value="">
                  {rolesQuery.isLoading ? "Loading roles…" : "Select a role…"}
                </option>
                {roles.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.code} — {r.displayName}
                  </option>
                ))}
              </Select>
            </FormField>
          ) : (
            <FormField label="Portal account type" required htmlFor="qa-account-type">
              <Select
                id="qa-account-type"
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
              >
                <option value="">Select a portal type…</option>
                {IMPERSONATABLE_AUDIENCES.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </Select>
            </FormField>
          )}

          {error && (
            <p className="m-0 text-caption text-status-error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

function ModeRadio({
  checked,
  onSelect,
  label,
}: {
  checked: boolean;
  onSelect: () => void;
  label: string;
}) {
  return (
    <label className="inline-flex items-center gap-space-2 text-small text-onyx">
      <input
        type="radio"
        name="qa-user-mode"
        checked={checked}
        onChange={onSelect}
        className="h-4 w-4 accent-gold"
      />
      {label}
    </label>
  );
}

function ResultRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-space-2">
      <dt className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
        {label}
      </dt>
      <dd className="col-span-2 m-0 text-small text-onyx">{children}</dd>
    </div>
  );
}
